import { useState, useCallback } from 'react';
import { message } from 'antd';
import { scApi, FC110, FC111 } from '../../../services/scApi';
import { useAuth } from '../../../contexts/AuthContext';
import { useFieldErrors } from '../../../hooks/useFieldErrors';
import { Mode } from './useSCMode';

export function useSCRecord() {
  const { user } = useAuth();
  const [record, setRecord] = useState<FC110 | null>(null);
  const [lastApiResponse, setLastApiResponse] = useState<FC110 | null>(null);
  const [formData, setFormData] = useState<Partial<FC110>>({});
  const [details, setDetails] = useState<Partial<FC111>[]>([]);
  const [selectedRow, setSelectedRow] = useState(0);
  const [loading, setLoading] = useState(false);
  const { fieldErrors, setFieldErrors, clearFieldErrors, getFieldError, hasErrors } = useFieldErrors();

  /** Load a single record by S/C No */
  const loadRecord = useCallback(async (scNo: string): Promise<{
    data: FC110;
    dets: Partial<FC111>[];
  } | null> => {
    setLoading(true);
    try {
      const data = await scApi.get(scNo);
      setRecord(data);
      setLastApiResponse(data);
      setFormData({ ...data });
      const dets = data.details || [];
      setDetails(dets);
      setSelectedRow(0);
      return { data, dets };
    } catch {
      message.error('載入失敗');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /** Set a single master field */
  const setField = useCallback((key: keyof FC110, value: FC110[keyof FC110]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  /** Get a single master field value */
  const getField = useCallback((key: keyof FC110): FC110[keyof FC110] => {
    return (formData as Record<string, FC110[keyof FC110]>)[key] ?? '';
  }, [formData]);

  /** Get a detail field value from the selected row */
  const getDetailField = useCallback((key: keyof FC111): FC111[keyof FC111] => {
    const sel = details[selectedRow] as Partial<FC111> | undefined;
    return sel ? (sel as Record<string, FC111[keyof FC111]>)[key] ?? '' : '';
  }, [details, selectedRow]);

  /** Set a detail field value on the selected row */
  const setDetailField = useCallback((key: keyof FC111, value: FC111[keyof FC111]) => {
    setDetails(prev => {
      const next = [...prev];
      if (next[selectedRow]) {
        next[selectedRow] = { ...next[selectedRow], [key]: value };
      }
      return next;
    });
  }, [selectedRow]);

  /** Save (create or update) */
  const handleSave = useCallback(async (
    mode: Mode,
    onSuccess: (result: FC110) => void,
  ) => {
    setLoading(true);
    try {
      const req = {
        master: formData as FC110,
        details: details as FC111[],
      };
      if (mode === 'add') {
        const result = await scApi.create(req);
        setLastApiResponse(result);
        message.success(`新增成功: ${result.fc110_01}`);
        onSuccess(result);
      } else {
        const result = await scApi.update(formData.fc110_01!, req);
        setLastApiResponse(result);
        message.success('修改成功');
        setRecord(result);
        setFormData({ ...result });
        setDetails(result.details || []);
        onSuccess(result);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { errors?: { field: string; message: string }[]; error?: string } } };
      const errors = err.response?.data?.errors;
      if (errors && Array.isArray(errors)) {
        setFieldErrors(errors);
        const summary = errors.slice(0, 5).map(e => e.message).join('\n');
        const extra = errors.length > 5 ? `\n...共 ${errors.length} 項` : '';
        message.error({ content: summary + extra, duration: 6, style: { whiteSpace: 'pre-line' } });
      } else {
        message.error(err.response?.data?.error || '儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [formData, details]);

  /** Delete the current record */
  const handleDelete = useCallback(async (): Promise<boolean> => {
    if (!record) return false;
    try {
      await scApi.delete(record.fc110_01);
      message.success('刪除成功');
      return true;
    } catch {
      message.error('刪除失敗');
      return false;
    }
  }, [record]);

  /** Add a new detail row */
  const addDetailRow = useCallback(() => {
    const maxSeq = details.reduce((max, d) => Math.max(max, d.fc111_02 || 0), 0);
    if (maxSeq >= 9) {
      message.warning('最多 9 個明細項');
      return;
    }
    setDetails(prev => [...prev, { fc111_02: maxSeq + 1, fc111_01: formData.fc110_01 }]);
  }, [details, formData.fc110_01]);

  /** Delete the currently selected detail row */
  const deleteDetailRow = useCallback(() => {
    if (details.length <= 1) return;
    setDetails(prev => {
      const next = prev.filter((_, i) => i !== selectedRow);
      return next.map((d, i) => ({ ...d, fc111_02: i + 1 }));
    });
    setSelectedRow(Math.max(0, selectedRow - 1));
  }, [details.length, selectedRow]);

  /** Reset form to current record (revert) */
  const revertToRecord = useCallback(() => {
    clearFieldErrors();
    if (record) {
      setFormData({ ...record });
      setDetails(record.details || []);
    }
  }, [record]);

  /** Reset form for a new add */
  const resetForAdd = useCallback(() => {
    clearFieldErrors();
    setRecord(null);
    setFormData({
      fc110_24: 'NTD',
      fc110_25: 1,
      fc110_58: 1,
      fc110_18: user?.userId || '',
    });
    setDetails([{ fc111_02: 1 }]);
    setSelectedRow(0);
  }, [user]);

  /** Apply result after save (for both add and edit) */
  const applyResult = useCallback((result: FC110) => {
    setRecord(result);
    setLastApiResponse(result);
    setFormData({ ...result });
    setDetails(result.details || []);
  }, []);

  /** Apply preview data imported from quotation without persisting it yet */
  const applyImportData = useCallback((preview: FC110) => {
    setRecord(null);
    setFormData({ ...preview });
    setDetails(preview.details || [{ fc111_02: 1 }]);
    setSelectedRow(0);
  }, []);

  return {
    record, lastApiResponse, formData, details, selectedRow, loading,
    setSelectedRow, setDetails,
    loadRecord, setField, getField,
    getDetailField, setDetailField,
    handleSave, handleDelete,
    addDetailRow, deleteDetailRow,
    revertToRecord, resetForAdd, applyResult, applyImportData,
    fieldErrors, getFieldError, clearFieldErrors, hasErrors,
  };
}
